'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Divider, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { UpdateCloudResourceConfigurationRequestSetDbBackupModeEnum } from '@repo/api-gw-sdk';
import { useRef, useState } from 'react';
import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { MainDialog } from '@/components/dialogs/mainDialog';
import { StatusTag } from '@/components/shared/statusTag';
import type { TagStatus } from '@/components/shared/statusTag';
import { useDAL } from '@/contexts/useDAL';
import { useWorkspace } from '@/contexts/useWorkspace';
import { DatabaseParametersDialog } from './steps/databaseParametersDialog';
import { PrivateLinkDialog } from './steps/privateLinkDialog';
import { SqlCommandsDialog } from './steps/sqlCommandsDialog';
import { useRdsCdcStatus } from './useRdsCdcStatus';
type OpenStep = 'privateLink' | 'parameters' | 'sql' | null;
const PROVISIONING_TOOLTIP = 'Eon is creating the VPC endpoint to your database and waiting for AWS to make it available. This usually takes a few minutes.';

// Names must match icons already referenced in the app: the iconify bundler only ships icons it finds
// in source, so an invented name renders nothing at all.
const statusIcons: Record<TagStatus, string> = {
  active: 'material-symbols-check-circle-outline-rounded',
  pending: 'material-symbols-ads-click',
  error: 'material-symbols-error-med-outline-rounded',
  inactive: 'material-symbols-error-med-outline-rounded'
};
const RdsCdcStepRow = t0 => {
  const $ = _c(32);
  const {
    index,
    title,
    description,
    status,
    statusLabel,
    statusTooltip,
    buttonText,
    disabledReason,
    onOpen
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      gap: "24px",
      alignItems: "flex-start",
      paddingX: "40px"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = `${index}.`;
  let t3;
  if ($[1] !== t2) {
    t3 = <Typography variant="subtitle1Strong">{t2}</Typography>;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      gap: "4px",
      flex: 1
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== title) {
    t5 = <Typography variant="subtitle1Strong">{title}</Typography>;
    $[4] = title;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== description) {
    t6 = <Typography variant="body1">{description}</Typography>;
    $[6] = description;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      marginTop: "4px"
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const t8 = statusTooltip ?? "";
  const t9 = statusIcons[status];
  let t10;
  if ($[9] !== status || $[10] !== statusLabel || $[11] !== t9) {
    t10 = <Stack><StatusTag status={status} label={statusLabel} icon={t9} /></Stack>;
    $[9] = status;
    $[10] = statusLabel;
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== t10 || $[14] !== t8) {
    t11 = <Stack direction="row" sx={t7}><Tooltip title={t8}>{t10}</Tooltip></Stack>;
    $[13] = t10;
    $[14] = t8;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t11 || $[17] !== t5 || $[18] !== t6) {
    t12 = <Stack sx={t4}>{t5}{t6}{t11}</Stack>;
    $[16] = t11;
    $[17] = t5;
    $[18] = t6;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const t13 = disabledReason ?? "";
  let t14;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      marginLeft: "auto"
    };
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  const t15 = !!disabledReason;
  let t16;
  if ($[21] !== buttonText || $[22] !== onOpen || $[23] !== t15) {
    t16 = <Stack sx={t14}><Button variant="outlined" disabled={t15} onClick={onOpen}>{buttonText}</Button></Stack>;
    $[21] = buttonText;
    $[22] = onOpen;
    $[23] = t15;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== t13 || $[26] !== t16) {
    t17 = <Tooltip title={t13}>{t16}</Tooltip>;
    $[25] = t13;
    $[26] = t16;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== t12 || $[29] !== t17 || $[30] !== t3) {
    t18 = <Stack direction="row" sx={t1}>{t3}{t12}{t17}</Stack>;
    $[28] = t12;
    $[29] = t17;
    $[30] = t3;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  return t18;
};
export const RdsCdcOnboardingDialog = ({
  resourceId,
  region,
  isCdcEnabled,
  onClose,
  onEnabled
}: {
  resourceId: string;
  region: string;
  /** CDC is already the resource's mode: the hub is a review surface, so no "Enable CDC" action. */
  isCdcEnabled: boolean;
  onClose: () => void;
  onEnabled: () => void;
}) => {
  const dal = useDAL();
  const theme = useTheme();
  const {
    snackbar
  } = useWorkspace();
  const [openStep, setOpenStep] = useState<OpenStep>(null);
  const [isEnabling, setIsEnabling] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Transitions animate only between the hub and the regular screen; hub<->step swaps run at zero
  // duration so the swap doesn't replay the fade as a flash. The enter side goes to zero once a swap
  // has happened; the exit side is handled by keeping the dialog mounted until the fade-out finishes,
  // with the pending close callback fired from onExited.
  const hasSwappedScreens = useRef(false);
  const [pendingClose, setPendingClose] = useState<(() => void) | null>(null);
  const showStep = (step: OpenStep) => {
    hasSwappedScreens.current = true;
    setOpenStep(step);
  };
  const closeToScreen = (afterFade: () => void) => {
    setPendingClose(() => afterFade);
  };

  // Prefetch only: the onboarding-instructions call takes seconds (it resolves AWS parameters and
  // provisions the replicator credentials), and every step dialog needs its result. Warming the
  // shared SWR key here makes the steps render instantly by the time one is opened.
  dal.inventory.privateLink.useOnboardRdsCdc(resourceId);
  const {
    isLoading,
    refresh,
    isPrivateLinkActive,
    isPrivateLinkProvisioning,
    isPrivateLinkTearingDown,
    parameterChecks,
    isPrivateLinkStepDone,
    isParametersStepDone,
    isSqlStepDone,
    allStepsDone
  } = useRdsCdcStatus(resourceId);
  const refreshStatus = async () => {
    setIsRefreshing(true);
    try {
      await refresh();
    } finally {
      setIsRefreshing(false);
    }
  };
  const enableCdc = async () => {
    setIsEnabling(true);
    try {
      await dal.inventory.configuration.update(resourceId, {
        setDbBackupMode: UpdateCloudResourceConfigurationRequestSetDbBackupModeEnum.Cdc
      });
      snackbar.showMessage('CDC enabled for this database');
      closeToScreen(onEnabled);
    } catch (error) {
      snackbar.showErrorMessage(await extractErrorMessage(error));
    } finally {
      setIsEnabling(false);
    }
  };
  if (openStep === 'privateLink') {
    return <PrivateLinkDialog resourceId={resourceId} onBack={() => showStep(null)} onClose={onClose} />;
  }
  if (openStep === 'parameters') {
    return <DatabaseParametersDialog resourceId={resourceId} region={region} onBack={() => showStep(null)} onClose={onClose} />;
  }
  if (openStep === 'sql') {
    return <SqlCommandsDialog resourceId={resourceId} onBack={() => showStep(null)} onClose={onClose} />;
  }
  const failedParameters = parameterChecks.filter(param => !param.ok).length;
  return <MainDialog isOpen={pendingClose === null} isLoading={isLoading} scrollableBody
  // Entering from a step swap is instant; leaving toward the regular screen always fades, so only
  // the enter side is zeroed once a swap has happened. exit stays explicit: a timeout object with
  // a missing key desyncs the transition callback from the CSS animation.
  transitionDuration={hasSwappedScreens.current ? {
    appear: 0,
    enter: 0,
    exit: theme.transitions.duration.leavingScreen
  } : undefined} onExited={pendingClose ?? undefined} header={{
    title: 'Set up CDC',
    subtitle: 'Complete these steps to capture changes continuously from this database.',
    onClose: () => closeToScreen(onClose)
  }} footer={{
    primaryButton: isCdcEnabled ? {
      text: 'Close',
      onClick: () => closeToScreen(onClose),
      'data-testid': 'rds-cdc-onboarding-close-button'
    } : {
      text: 'Enable CDC',
      onClick: enableCdc,
      disabled: !allStepsDone || isEnabling,
      disabledTooltip: allStepsDone ? undefined : 'Complete all three steps first',
      'data-testid': 'rds-cdc-onboarding-enable-button'
    },
    secondaryButton: isCdcEnabled ? undefined : {
      text: 'Close',
      onClick: () => closeToScreen(onClose),
      variant: 'outlined',
      'data-testid': 'rds-cdc-onboarding-close-button'
    },
    submitInProgress: isEnabling,
    loadingText: 'Enabling CDC...',
    children: <ButtonWithIcon variant='text' icon='material-symbols-refresh-rounded' text={isRefreshing ? 'Refreshing...' : 'Refresh status'} disabled={isRefreshing} onClick={() => void refreshStatus()} sx={{
      '@keyframes refreshSpin': {
        from: {
          transform: 'rotate(0deg)'
        },
        to: {
          transform: 'rotate(360deg)'
        }
      },
      '& i': isRefreshing ? {
        animation: 'refreshSpin 1s linear infinite'
      } : {}
    }} data-testid='rds-cdc-onboarding-refresh-button' />
  }}>
      <Stack divider={<Divider />} sx={{
      gap: '24px'
    }}>
        <RdsCdcStepRow index={1} title='Configure AWS PrivateLink' description='Creates the resource gateway and RAM share Eon uses to reach your database.' status={isPrivateLinkStepDone ? 'active' : isPrivateLinkProvisioning || isPrivateLinkTearingDown ? 'pending' : 'error'} statusLabel={isPrivateLinkStepDone ? 'Active' : isPrivateLinkProvisioning ? 'Provisioning' : isPrivateLinkTearingDown ? 'Tearing down' : 'Not configured'} statusTooltip={isPrivateLinkProvisioning ? PROVISIONING_TOOLTIP : isPrivateLinkTearingDown ? 'Eon is removing the VPC endpoint to your database.' : undefined} buttonText={isPrivateLinkStepDone ? 'Review' : 'Set up'} onOpen={() => showStep('privateLink')} />
        <RdsCdcStepRow index={2} title='Set database parameters' description='Enables logical replication on the instance parameter group.' status={isParametersStepDone ? 'active' : 'error'} statusLabel={isParametersStepDone ? 'All parameters set' : parameterChecks.length === 0 ? 'Parameters could not be read' : `${failedParameters} of ${parameterChecks.length} parameters not set`} buttonText={isParametersStepDone ? 'Review' : 'Set up'} onOpen={() => showStep('parameters')} />
        <RdsCdcStepRow index={3} title='Run SQL commands on the database' description='Creates the replication user, publication, and change-capture triggers.' status={isSqlStepDone ? 'active' : 'error'} statusLabel={isSqlStepDone ? 'Verified' : 'Not verified'} buttonText={isSqlStepDone ? 'Review' : 'Set up'}
      // Verification connects to the database over PrivateLink, so it cannot run before step 1.
      disabledReason={isPrivateLinkActive ? undefined : 'Available once AWS PrivateLink is active'} onOpen={() => showStep('sql')} />
      </Stack>
    </MainDialog>;
};